/**
 * SubstanzRadarAktionenTab — specialized UI for the "Substance Radar" monitor AP.
 *
 * Simulates an AI-powered regulatory web-search monitor. One finding covers all
 * potentially affected suppliers. Two actions:
 *   1. Import website     → stage_documents (implemented)
 *   2. Inquiry form       → prepare_inquiry_form (placeholder, not yet wired)
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  IconCheck,
  IconChevronLeft,
  IconExternalLink,
  IconLoader2,
  IconRadar,
  IconRefresh,
} from "@tabler/icons-react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useEffect, useState } from "react"
import type { MonitorFindingOut } from "../../../api/client"
import { api } from "../../../api/client"
import { useStore } from "../../../store/useStore"

const AP_ID = "substanz-radar"

// ---------------------------------------------------------------------------
// Idle list view
// ---------------------------------------------------------------------------

function IdleView({
  onSelectFinding,
}: {
  onSelectFinding: (f: MonitorFindingOut) => void
}) {
  const { translate, currentLanguage } = useTranslation()
  const qc = useQueryClient()
  const [evaluating, setEvaluating] = useState(false)
  const { setMonitorOpenCount } = useStore()

  const { data: openFindings = [], refetch } = useQuery({
    queryKey: ["monitor-findings-open", AP_ID],
    queryFn: () => api.monitorListFindings(AP_ID, "open"),
    staleTime: 30_000,
  })

  // Sync open count to store for AP card badge
  useEffect(() => {
    setMonitorOpenCount(AP_ID, openFindings.length)
  }, [openFindings.length, setMonitorOpenCount])

  // Lazy evaluation on mount
  useEffect(() => {
    api
      .monitorEvaluate(AP_ID)
      .then(() => {
        refetch()
        qc.invalidateQueries({ queryKey: ["ap-activity"] })
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleEvaluate() {
    setEvaluating(true)
    try {
      await api.monitorEvaluate(AP_ID)
      refetch()
      qc.invalidateQueries({ queryKey: ["ap-activity"] })
    } finally {
      setEvaluating(false)
    }
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      {/* Description banner */}
      <div className="space-y-1.5 rounded-lg border border-grey4 bg-grey1 px-4 py-3">
        <div className="flex items-center gap-2">
          <IconRadar size={15} className="shrink-0 text-yellow1" />
          <p className="text-sm font-medium text-white">
            {translate("QMS/MONITOR/SUBSTANCE_RADAR/TITLE")}
          </p>
        </div>
        <p className="text-xs leading-relaxed text-grey5">
          {translate("QMS/MONITOR/SUBSTANCE_RADAR/DESCRIPTION")}
        </p>
        <button
          onClick={handleEvaluate}
          disabled={evaluating}
          className="flex items-center gap-1.5 rounded-md border border-grey4 bg-grey2 px-3 py-1.5 text-xs text-grey6 transition-colors hover:bg-grey3 disabled:opacity-50"
        >
          <IconRefresh size={12} className={evaluating ? "animate-spin" : ""} />
          {translate("QMS/MONITOR/CHECK_NOW")}
        </button>
      </div>

      {/* Open findings */}
      <section>
        <div className="mb-2 flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-grey5">
            {translate("QMS/MONITOR/CURRENT_FINDINGS")}
          </span>
          {openFindings.length > 0 && (
            <span className="rounded-full bg-yellow1/20 px-1.5 py-0.5 text-[10px] font-semibold text-yellow1">
              {openFindings.length}
            </span>
          )}
        </div>

        {openFindings.length === 0 ? (
          <p className="px-1 text-sm italic text-grey5">
            {translate("QMS/MONITOR/NO_OPEN_FINDINGS")}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {openFindings.map((f) => (
              <FindingCard
                key={f.id}
                finding={f}
                onClick={() => onSelectFinding(f)}
                locale={localeFromLanguage(currentLanguage)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function FindingCard({
  finding,
  onClick,
  locale,
}: {
  finding: MonitorFindingOut
  onClick: () => void
  locale: string
}) {
  const { translate } = useTranslation()
  const narrative = finding.evidence.narrative as string | undefined
  const substance = finding.evidence.substance_name as string | undefined
  const date = finding.evidence.effective_date as string | undefined

  return (
    <button
      onClick={onClick}
      className="space-y-1.5 rounded-lg border border-grey4 bg-grey1 px-4 py-3 text-left transition-colors hover:border-grey5 hover:bg-grey2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="shrink-0 text-sm text-yellow1">🚩</span>
          <span className="truncate text-sm font-medium text-white">
            {finding.summary}
          </span>
        </div>
        <span className="shrink-0 whitespace-nowrap text-xs text-grey5">
          {formatDate(date, locale)}
        </span>
      </div>
      {substance && (
        <p className="text-xs text-grey5">
          {translate("QMS/MONITOR/SUBSTANCE")}: {substance}
        </p>
      )}
      {narrative && (
        <p className="line-clamp-2 text-xs leading-relaxed text-grey6">
          {narrative}
        </p>
      )}
      <div className="flex items-center gap-1 pt-0.5">
        <span className="text-xs font-medium text-yellow1/80">
          {translate("QMS/MONITOR/REVIEW_FINDING")}
        </span>
      </div>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Detail / review view
// ---------------------------------------------------------------------------

function DetailView({
  finding,
  onBack,
  onRefresh,
}: {
  finding: MonitorFindingOut
  onBack: () => void
  onRefresh: () => void
}) {
  const { translate, currentLanguage } = useTranslation()
  const locale = localeFromLanguage(currentLanguage)
  const qc = useQueryClient()
  const { selectRecords } = useStore()
  const [applying, setApplying] = useState<string | null>(null)
  const [applied, setApplied] = useState<Set<string>>(new Set())

  const evidence = finding.evidence
  const narrative = evidence.narrative as string | undefined
  const whyRelevant = evidence.why_relevant as string | undefined
  // The evaluator returns the generic three-supplier shape. The demo
  // narrative wants one supplier / one Befund — clip at the render
  // boundary so the BE evaluator stays generic.
  const allAffectedNames =
    (evidence.affected_supplier_names as string[] | undefined) ?? []
  const affectedNames = allAffectedNames.slice(0, 1)
  const citation = evidence.citation as string | undefined
  const date = evidence.effective_date as string | undefined
  const sourceUrl = evidence.source_url as string | undefined

  const rec1 = finding.recommendations.find(
    (r) => r.target_action === "stage_documents",
  )
  const clippedTargetIds = rec1 ? rec1.target_record_ids.slice(0, 1) : []

  const rec1Applied = rec1
    ? applied.has(rec1.id) || rec1.state === "applied"
    : false

  async function handleApplyRec1() {
    if (!rec1 || rec1Applied) return
    setApplying(rec1.id)
    try {
      await api.monitorApplyRecommendation(AP_ID, finding.id, rec1.id)
      // Cascade-stale the (clipped) affected supplier(s) + every component
      // they deliver via the demo primitive. Customer freshness walks
      // customer→product→component→substance (never visits suppliers), so
      // staling the component layer is what actually flips the customer.
      for (const supplierId of clippedTargetIds) {
        await api.monitorSupplierCascade({
          supplier_record_id: supplierId,
          direction: 'stale',
        })
      }
      // Mark the affected suppliers as in-progress in the supplier AP so the
      // user sees them as buckets without manual selection.
      if (clippedTargetIds.length > 0) {
        selectRecords(clippedTargetIds)
      }
      setApplied((prev) => new Set([...prev, rec1.id]))
      onRefresh()
      qc.invalidateQueries({ queryKey: ["ap-activity"] })
      qc.invalidateQueries({ queryKey: ["pendingDocuments", "supplier"] })
      qc.invalidateQueries({ queryKey: ["monitor-findings-open", AP_ID] })
      // ExporterAktionenTab (used by the supplier AP) reads its Ausstehend
      // bucket from ['ap-partition', apId, threshold]. The pendingDocuments
      // invalidation above is necessary but not sufficient.
      qc.invalidateQueries({ queryKey: ["ap-partition"] })
      // The cascade stales the supplier + components, which flips customer
      // freshness on the next read. Invalidate so the landing tile flips
      // from "1/1 ready" to "0/1 ready" without waiting on the poll.
      qc.invalidateQueries({ queryKey: ["freshness"] })
    } finally {
      setApplying(null)
    }
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex shrink-0 items-center gap-2 border-b border-grey4 px-4 py-2.5">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-grey5 transition-colors hover:text-white"
        >
          <IconChevronLeft size={14} />
          {translate("QMS/COMMON/BACK")}
        </button>
        <span className="text-grey4">|</span>
        <div className="flex min-w-0 items-center gap-1.5">
          <IconRadar size={14} className="shrink-0 text-yellow1" />
          <span className="truncate text-sm font-medium text-white">
            {translate("QMS/MONITOR/FINDING_HEADER")}
          </span>
        </div>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto p-4">
        {/* Regulatory signal */}
        <section className="space-y-1.5 rounded-lg border border-yellow1/20 bg-yellow1/5 px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-yellow1">
              {translate("QMS/MONITOR/REGULATORY_SIGNAL")}
            </span>
            {date && (
              <span className="shrink-0 text-xs text-grey5">
                {formatDate(date, locale)}
              </span>
            )}
          </div>
          {citation && (
            <p className="text-sm leading-relaxed text-white">{citation}</p>
          )}
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 inline-flex items-center gap-1 text-xs hover:underline"
            >
              <IconExternalLink size={11} />
              {translate("QMS/MONITOR/OPEN_SOURCE")}
            </a>
          )}
        </section>

        {/* Narrative explanation */}
        <section className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-grey5">
            {translate("QMS/MONITOR/WHY_RELEVANT")}
          </p>
          {narrative && (
            <p className="text-sm leading-relaxed text-grey6">{narrative}</p>
          )}
          {whyRelevant && (
            <p className="text-sm leading-relaxed text-grey6">{whyRelevant}</p>
          )}
        </section>

        {/* Affected suppliers */}
        {affectedNames.length > 0 && (
          <section className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-grey5">
              {translate("QMS/MONITOR/AFFECTED_SUPPLIERS", {
                count: affectedNames.length,
              })}
            </p>
            <div className="divide-y divide-grey4/50 rounded-lg border border-grey4 bg-grey1">
              {affectedNames.map((name, i) => (
                <div key={i} className="px-3 py-2 text-sm text-grey6">
                  {name}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Recommendation */}
        <section className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-grey5">
            {translate("QMS/MONITOR/RECOMMENDATION")}
          </p>

          {rec1 && (
            <RecommendationBlock
              index={1}
              title={translate("QMS/MONITOR/IMPORT_WEBSITE_TITLE")}
              description={translate("QMS/MONITOR/IMPORT_WEBSITE_DESCRIPTION")}
              applied={rec1Applied}
              loading={applying === rec1.id}
              onApply={handleApplyRec1}
              applyLabel={translate("QMS/MONITOR/IMPORT_DOCUMENT")}
            />
          )}
        </section>
      </div>
    </div>
  )
}

function RecommendationBlock({
  index,
  title,
  description,
  applied,
  loading,
  onApply,
  applyLabel,
  disabled = false,
}: {
  index: number
  title: string
  description: string
  applied: boolean
  loading: boolean
  onApply: () => void
  applyLabel: string
  disabled?: boolean
}) {
  const { translate } = useTranslation()
  return (
    <div
      className={clsx(
        "space-y-2 rounded-lg border px-4 py-3 transition-colors",
        applied ? "border-success/30 bg-success/5" : "border-grey4 bg-grey1",
      )}
    >
      <div className="flex items-start gap-2">
        <span
          className={clsx(
            "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
            applied ? "bg-success/20 text-success" : "bg-grey3 text-grey5",
          )}
        >
          {index}
        </span>
        <p className="text-sm font-medium leading-snug text-white">{title}</p>
      </div>
      <p className="pl-7 text-xs leading-relaxed text-grey5">{description}</p>
      <div className="pl-7">
        {applied ? (
          <div className="flex items-center gap-1.5 text-xs text-success">
            <IconCheck size={12} />
            {translate("QMS/MONITOR/APPLIED")}
          </div>
        ) : (
          <button
            onClick={onApply}
            disabled={disabled || loading}
            className={clsx(
              "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
              disabled
                ? "cursor-not-allowed border border-grey4 bg-grey2 text-grey5 opacity-60"
                : "bg-yellow1 text-black hover:bg-yellow1/90 disabled:opacity-50",
            )}
          >
            {loading && <IconLoader2 size={12} className="animate-spin" />}
            {applyLabel}
          </button>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------

export function SubstanzRadarAktionenTab() {
  const [activeFinding, setActiveFinding] = useState<MonitorFindingOut | null>(
    null,
  )
  const { data: openFindings = [], refetch } = useQuery({
    queryKey: ["monitor-findings-open", AP_ID],
    queryFn: () => api.monitorListFindings(AP_ID, "open"),
    staleTime: 30_000,
  })

  function handleRefresh() {
    refetch()
    if (activeFinding) {
      const updated = openFindings.find((f) => f.id === activeFinding.id)
      if (updated) setActiveFinding(updated)
    }
  }

  if (activeFinding) {
    return (
      <DetailView
        finding={activeFinding}
        onBack={() => setActiveFinding(null)}
        onRefresh={handleRefresh}
      />
    )
  }

  return <IdleView onSelectFinding={setActiveFinding} />
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function localeFromLanguage(language: string): string {
  if (language === "EN") return "en-US"
  if (language === "JA") return "ja-JP"
  return "de-DE"
}

function formatDate(iso: string | undefined | null, locale: string) {
  if (!iso) return ""
  try {
    return new Date(iso).toLocaleDateString(locale)
  } catch {
    return iso
  }
}
